/**
 * Error Boundary Component
 * Comprehensive error handling with recovery mechanisms
 */

'use client';

import React, { Component, ReactNode, ErrorInfo } from 'react';
import { Typography } from '@/shared/components/typography/SafeTypography'
import { Button, Card, Alert, Space, Divider } from 'antd';
import { ExclamationCircleOutlined, ReloadOutlined, HomeOutlined, BugOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

// =============================================================================
// TYPES & INTERFACES
// =============================================================================

interface ErrorBoundaryProps {
  /** Child components to render */
  children: ReactNode;
  /** Fallback component to render on error */
  fallback?: (error: Error, errorInfo: ErrorInfo, retry: () => void) => ReactNode;
  /** Error reporting callback */
  onError?: (error: Error, errorInfo: ErrorInfo, errorId: string) => void;
  /** Maximum retry attempts */
  maxRetries?: number;
  /** Whether to show detailed error information */
  showDetails?: boolean;
}

interface ErrorBoundaryState {
  /** Whether an error has occurred */
  hasError: boolean;
  /** The error object */
  error: Error | null;
  /** React error info */
  errorInfo: ErrorInfo | null;
  /** Unique error identifier */
  errorId: string | null;
  /** Number of retry attempts */
  retryAttempts: number;
  /** Error timestamp */
  timestamp: Date;
}

// =============================================================================
// ERROR BOUNDARY COMPONENT
// =============================================================================

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private retryTimeoutId: NodeJS.Timeout | null = null;

  constructor(props: ErrorBoundaryProps) {
    super(props);

    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      retryAttempts: 0,
      timestamp: new Date(),
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorId: `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
    };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error details
    console.error('ErrorBoundary caught an error:', error);
    console.error('Error info:', errorInfo);

    // Update state with error info
    this.setState({
      errorInfo,
    });

    // Report error if callback provided
    if (this.props.onError && this.state.errorId) {
      this.props.onError(error, errorInfo, this.state.errorId);
    }

    // Log to external service in production
    if (process.env.NODE_ENV === 'production') {
      // Here you would typically send to an error reporting service
      // like Sentry, LogRocket, or your own error tracking system
      this.logErrorToService(error, errorInfo);
    }
  }

  override componentWillUnmount() {
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
    }
  }

  private logErrorToService = (error: Error, errorInfo: ErrorInfo) => {
    // Placeholder for external error logging service
    // In a real app, you'd integrate with Sentry, LogRocket, etc.
    const errorData = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      errorId: this.state.errorId,
      timestamp: this.state.timestamp,
      userAgent: navigator.userAgent,
      url: window.location.href,
    };

    // Example: Send to your error tracking service
    // errorTrackingService.captureException(errorData);
    console.log('Error logged to service:', errorData);
  };

  private handleRetry = () => {
    const maxRetries = this.props.maxRetries || 3;

    if (this.state.retryAttempts >= maxRetries) {
      return;
    }

    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      retryAttempts: prevState.retryAttempts + 1,
    }));

    // Optional: Add a small delay before retry
    this.retryTimeoutId = setTimeout(() => {
      // Force a re-render
      this.forceUpdate();
    }, 100);
  };

  private handleGoHome = () => {
    // Navigate to home page
    if (typeof window !== 'undefined') {
      window.location.href = '/';
    }
  };

  override render() {
    const { hasError, error, errorInfo, errorId, retryAttempts, timestamp } = this.state;
    const { children, fallback, maxRetries = 3, showDetails = true } = this.props;

    if (hasError && error) {
      // Use custom fallback if provided
      if (fallback) {
        return fallback(error, errorInfo!, this.handleRetry);
      }

      // Default error UI
      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          padding: '16px'
        }}>
          <Card style={{ width: '100%', maxWidth: '600px' }}>
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{
                margin: '0 auto 16px',
                width: '48px',
                height: '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                backgroundColor: '#fff2f0',
              }}>
                <ExclamationCircleOutlined style={{ fontSize: '24px', color: '#ff4d4f' }} />
              </div>
              <Title level={3} style={{ margin: '16px 0 8px', color: '#262626' }}>
                Something went wrong
              </Title>
              <Text type="secondary">
                We apologize for the inconvenience. An unexpected error has occurred.
              </Text>
            </div>

            {/* Error Details */}
            {showDetails && (
              <Alert
                icon={<BugOutlined />}
                type="error"
                showIcon
                title="Error Details"
                description={
                  <Space orientation="vertical" style={{ width: '100%' }}>
                    <div>
                      <Text strong>Error:</Text> {error.message || 'Unknown error'}
                    </div>
                    <div>
                      <Text strong>Component:</Text> {errorInfo?.componentStack?.split('\n')[1]?.trim() || 'Unknown'}
                    </div>
                    <div>
                      <Text strong>Time:</Text> {timestamp.toLocaleString()}
                    </div>
                    {errorId && (
                      <div>
                        <Text strong>Error ID:</Text> {errorId}
                      </div>
                    )}
                  </Space>
                }
                style={{ marginBottom: '24px' }}
              />
            )}

            {/* Recovery Actions */}
            <Space style={{ width: '100%', marginBottom: '16px' }}>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                onClick={this.handleRetry}
                disabled={retryAttempts >= maxRetries}
                style={{ flex: 1 }}
              >
                {retryAttempts >= maxRetries ? 'Max retries reached' : `Try Again (${retryAttempts}/${maxRetries})`}
              </Button>

              <Button
                icon={<HomeOutlined />}
                onClick={this.handleGoHome}
                style={{ flex: 1 }}
              >
                Go to Home
              </Button>
            </Space>

            {/* Development Info */}
            {process.env.NODE_ENV === 'development' && errorInfo && showDetails && (
              <>
                <Divider />
                <details style={{ marginBottom: '16px' }}>
                  <summary style={{ cursor: 'pointer', fontWeight: 500, color: '#595959' }}>
                    Stack Trace (Development)
                  </summary>
                  <pre style={{
                    marginTop: '8px',
                    fontSize: '12px',
                    color: '#8c8c8c',
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    backgroundColor: '#f5f5f5',
                    padding: '8px',
                    borderRadius: '4px'
                  }}>
                    {error.stack}
                  </pre>
                  <pre style={{
                    marginTop: '8px',
                    fontSize: '12px',
                    color: '#8c8c8c',
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                    backgroundColor: '#f5f5f5',
                    padding: '8px',
                    borderRadius: '4px'
                  }}>
                    {errorInfo.componentStack}
                  </pre>
                </details>
              </>
            )}

            {/* Help Text */}
            {showDetails && (
              <>
                <Divider />
                <div style={{ textAlign: 'center' }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    If this problem persists, please contact support with Error ID: {errorId}
                  </Text>
                </div>
              </>
            )}
          </Card>
        </div>
      );
    }

    return children;
  }
}

// =============================================================================
// FUNCTIONAL ERROR BOUNDARY WRAPPER
// =============================================================================

interface ErrorBoundaryWrapperProps extends Omit<ErrorBoundaryProps, 'children'> {
  children: ReactNode;
}

/**
 * Functional wrapper for the ErrorBoundary class component
 */
export const ErrorBoundaryWrapper: React.FC<ErrorBoundaryWrapperProps> = ({
  children,
  ...props
}) => {
  return (
    <ErrorBoundary {...props}>
      {children}
    </ErrorBoundary>
  );
};

// Default export
export default ErrorBoundary;
